import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, Link, Typography } from '@material-ui/core';
import moment from 'moment';

import { InfiniteQuery } from 'types/app';
import { GetUserSearchesV2QueryVariables, UserProfileSearchFragment } from 'api';
import { DataTable, DataTableActions, DataTableColumn, UserAvatar } from 'components';
import { generateLink } from 'Links';

import Actions from './Actions';

interface Props extends InfiniteQuery<UserProfileSearchFragment> {
  active?: boolean;
  queryVariables: GetUserSearchesV2QueryVariables;
}

const UserManagementTable: FC<Props> = ({
  sentinelRef,
  results,
  isExhausted,
  isInitialLoad,
  queryVariables,
}) => {
  const { t } = useTranslation();

  function getName(profile: UserProfileSearchFragment) {
    return (
      <Grid container alignItems="center" wrap="nowrap" spacing={2}>
        <Grid item>
          <UserAvatar size={50} link user={profile as any} />
        </Grid>
        <Grid item container zeroMinWidth>
          <Link
            title={profile.full_name}
            component={generateLink(`/profile/${profile.id}`)}
            variant="subtitle2"
            noWrap
          >
            {profile.full_name}
          </Link>
        </Grid>
      </Grid>
    );
  }

  const columns: DataTableColumn<UserProfileSearchFragment>[] = [
    {
      width: 300,
      headerCell: {
        render: t('common:name'),
      },
      bodyCell: {
        render: profile => getName(profile),
      },
    },
    {
      width: 300,
      headerCell: {
        render: t('common:email'),
      },
      bodyCell: {
        render: profile => <Typography>{profile.email_address}</Typography>,
      },
    },
    {
      width: 150,
      headerCell: {
        render: t('common:title'),
      },
      bodyCell: {
        render: profile => <Typography>{profile.job_title}</Typography>,
      },
    },
    {
      width: 150,
      headerCell: {
        render: t('common:department'),
      },
      bodyCell: {
        render: profile => <Typography>{profile.department_title}</Typography>,
      },
    },
    {
      width: 150,
      headerCell: {
        render: t('common:location'),
      },
      bodyCell: {
        render: profile => <Typography>{profile.location_title}</Typography>,
      },
    },
    {
      width: 115,
      headerCell: {
        render: t('common:role'),
      },
      bodyCell: {
        render: profile => (
          <Typography style={{ textTransform: 'capitalize' }}>{profile.user_type}</Typography>
        ),
      },
    },
    {
      width: 150,
      headerCell: {
        render: t('admin:lastActive'),
      },
      bodyCell: {
        render: profile => {
          const mom = moment.unix(profile.last_accessed_at);
          return <Typography>{mom.format('l')}</Typography>;
        },
      },
    },
  ];

  const pagination = {
    isInitialLoad,
    isExhausted,
    sentinelRef,
  };

  const actions: DataTableActions<UserProfileSearchFragment> = userProfile => {
    return <Actions userProfile={userProfile} count={1} queryVariables={queryVariables} />;
  };

  return (
    <DataTable
      data={results}
      columns={columns}
      actions={actions}
      pagination={pagination}
      rowSelection={null}
    />
  );
};

export default UserManagementTable;
